import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Car, Plus, Trash2, Download, FileText, AlertTriangle, CheckCircle2, Wrench, Calendar } from "lucide-react";

const DOC_CATS = [
  { value: "libretto", label: "Libretto", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { value: "assicurazione", label: "Assicurazione", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "bollo", label: "Bollo", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "revisione", label: "Revisione", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "altro", label: "Altro", cls: "bg-slate-50 text-slate-700 border-slate-200" },
];

const DEADLINE_TYPES = [
  { value: "revisione", label: "Revisione", icon: Wrench },
  { value: "assicurazione", label: "Assicurazione", icon: FileText },
  { value: "bollo", label: "Bollo", icon: Calendar },
  { value: "tagliando", label: "Tagliando", icon: Wrench },
  { value: "altro", label: "Altro", icon: FileText },
];

export default function AutomezziPage() {
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selected, setSelected] = useState(null);
  const [docs, setDocs] = useState([]);
  const [deadlines, setDeadlines] = useState([]);

  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ plate: "", name: "", model: "", technician_id: "" });

  const [openDoc, setOpenDoc] = useState(false);
  const [docForm, setDocForm] = useState({ category: "libretto", title: "", expiry_date: "", file: null });

  const [openDl, setOpenDl] = useState(false);
  const [dlForm, setDlForm] = useState({ type: "revisione", due_date: "", notes: "" });

  const load = async () => {
    const v = (await api.get("/vehicles")).data;
    setVehicles(v);
    if (!selected && v.length > 0) setSelected(v[0]);
  };
  const loadTechs = async () => {
    // Include tutti i ruoli assegnabili: admin, magazziniere, assistente, tecnico
    const roles = ["admin", "magazziniere", "assistente", "tecnico", "subappalto"];
    const results = await Promise.all(roles.map((r) => api.get("/users", { params: { role: r } }).catch(() => ({ data: [] }))));
    const flat = results.flatMap((r) => r.data);
    // Dedup per id (nel caso un utente comparisse in più chiamate)
    const seen = new Set();
    const unique = flat.filter((u) => (seen.has(u.id) ? false : (seen.add(u.id), true)));
    // Ordina per ruolo (admin prima), poi per nome
    const roleOrder = { admin: 0, magazziniere: 1, assistente: 2, tecnico: 3, subappalto: 4 };
    unique.sort((a, b) => (roleOrder[a.role] ?? 9) - (roleOrder[b.role] ?? 9) || (a.name || "").localeCompare(b.name || ""));
    setTechnicians(unique);
  };

  const loadDetails = async (vid) => {
    const [dRes, deadRes] = await Promise.all([
      api.get("/vehicle-documents", { params: { vehicle_id: vid } }),
      api.get("/vehicle-deadlines", { params: { vehicle_id: vid } }),
    ]);
    setDocs(dRes.data);
    setDeadlines(deadRes.data);
  };

  useEffect(() => { load(); loadTechs(); }, []);
  useEffect(() => { if (selected) loadDetails(selected.id); }, [selected]);

  const createVehicle = async () => {
    if (!form.plate.trim()) return toast.error("Targa richiesta");
    try {
      const { data } = await api.post("/vehicles", { ...form, technician_id: form.technician_id || null });
      toast.success("Automezzo creato");
      setOpenNew(false);
      setForm({ plate: "", name: "", model: "", technician_id: "" });
      await load();
      setSelected(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Eliminare l'automezzo e tutti i suoi documenti/scadenze?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success(t("common.deleted"));
      setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reassignVehicle = async (id, technicianId) => {
    try {
      const { data } = await api.patch(`/vehicles/${id}`, { technician_id: technicianId });
      toast.success(technicianId ? `Riassegnato a ${techNameFor(technicianId)}` : "Assegnazione rimossa");
      setSelected(data);
      // aggiorna anche la lista laterale
      setVehicles((prev) => prev.map((v) => (v.id === id ? data : v)));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadDoc = async () => {
    if (!selected) return;
    if (!docForm.file || !docForm.title.trim()) return toast.error("Titolo e file richiesti");
    const fd = new FormData();
    fd.append("vehicle_id", selected.id);
    fd.append("category", docForm.category);
    fd.append("title", docForm.title);
    if (docForm.expiry_date) fd.append("expiry_date", docForm.expiry_date);
    fd.append("file", docForm.file);
    try {
      await api.post("/vehicle-documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setOpenDoc(false);
      setDocForm({ category: "libretto", title: "", expiry_date: "", file: null });
      loadDetails(selected.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadDoc = async (d) => {
    try {
      const res = await api.get(`/vehicle-documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Eliminare documento?")) return;
    try { await api.delete(`/vehicle-documents/${id}`); loadDetails(selected.id); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createDeadline = async () => {
    if (!selected) return;
    if (!dlForm.due_date) return toast.error(t("auto.data_scadenza_richiesta"));
    try {
      await api.post("/vehicle-deadlines", { ...dlForm, vehicle_id: selected.id });
      toast.success(t("auto.scadenza_aggiunta"));
      setOpenDl(false);
      setDlForm({ type: "revisione", due_date: "", notes: "" });
      loadDetails(selected.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markDone = async (id) => {
    try {
      await api.post(`/vehicle-deadlines/${id}/mark-done`);
      toast.success(t("auto.segnata_come_eseguita"));
      loadDetails(selected.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteDeadline = async (id) => {
    if (!window.confirm("Eliminare la scadenza?")) return;
    try { await api.delete(`/vehicle-deadlines/${id}`); loadDetails(selected.id); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deadlineStatus = (d) => {
    if (d.status === "done") return { cls: "bg-slate-100 text-slate-500 border-slate-200", label: t("auto.eseguita_status") };
    const today = new Date();
    const due = new Date(d.due_date);
    const days = Math.ceil((due - today) / 86400000);
    if (days < 0) return { cls: "bg-red-100 text-red-800 border-red-300", label: `${t("auto.scaduta_status")} (${t("auto.days_ago", { days: Math.abs(days) })})`, alert: true };
    if (days <= 7) return { cls: "bg-orange-100 text-orange-800 border-orange-300", label: `⚠ ${t("auto.scade_tra_g", { days })}`, alert: true };
    return { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: t("auto.valida_g", { days }) };
  };

  const techNameFor = (id) => technicians.find((tech) => tech.id === id)?.name || "—";
  const roleLabel = (role) => ({ admin: t("auto.role_admin"), magazziniere: t("auto.role_magazziniere"), assistente: t("auto.role_assistente"), tecnico: t("auto.role_tecnico"), subappalto: "Subappalto" }[role] || role || "");
  const roleBadgeCls = (role) => ({
    admin: "bg-purple-100 text-purple-700 border-purple-200",
    magazziniere: "bg-blue-100 text-blue-700 border-blue-200",
    assistente: "bg-amber-100 text-amber-700 border-amber-200",
    tecnico: "bg-emerald-100 text-emerald-700 border-emerald-200",
    subappalto: "bg-orange-100 text-orange-700 border-orange-200",
  }[role] || "bg-slate-100 text-slate-700 border-slate-200");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.parco_mezzi")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.vehicles")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auto.libretto_assicurazione_revisione_con_alert_automatici_alla_s")}</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-vehicle"><Plus className="mr-2 h-4 w-4" /> {t("auto.nuovo_automezzo_btn")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("auto.nuovo_automezzo")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Targa *</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} placeholder="AB123CD" className="rounded-sm font-mono" data-testid="veh-plate" /></div>
              <div><Label>Nome / Descrizione</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Furgone Mario" className="rounded-sm" data-testid="veh-name" /></div>
              <div><Label>Modello</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder={t("auto.es_fiat_doblò_16_mjt")} className="rounded-sm" /></div>
              <div>
                <Label>{t("auto.assegnato_a")}</Label>
                <Select value={form.technician_id || "__none__"} onValueChange={(v) => setForm({ ...form, technician_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm" data-testid="veh-tech"><SelectValue placeholder={t("auto.nessuno")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                    {technicians.map((tech) => (
                      <SelectItem key={tech.id} value={tech.id}>
                        <span className="flex items-center gap-2">
                          <span>{tech.name}</span>
                          <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${roleBadgeCls(tech.role)}`}>{roleLabel(tech.role)}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={createVehicle} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="veh-save">Crea</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {vehicles.length === 0 && (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-400">
          {t("auto.nessun_automezzo_hint")}
        </div>
      )}

      {vehicles.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
          <div className="space-y-1 rounded-sm border border-slate-200 bg-white p-2">
            {vehicles.map((v) => {
              const isSel = selected?.id === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => setSelected(v)}
                  data-testid={`veh-item-${v.id}`}
                  className={`flex w-full items-center gap-3 rounded-sm px-3 py-2 text-left transition-colors ${isSel ? "bg-[#0052FF] text-white" : "hover:bg-slate-100"}`}
                >
                  <Car className={`h-4 w-4 flex-shrink-0 ${isSel ? "text-white" : "text-slate-400"}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono font-semibold">{v.plate}</div>
                    <div className={`truncate text-[11px] ${isSel ? "text-blue-100" : "text-slate-500"}`}>{v.model || v.name || techNameFor(v.technician_id)}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="space-y-4">
              <div className="rounded-sm border border-slate-200 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Car className="h-5 w-5 text-[#0052FF]" />
                      <div className="font-mono text-2xl font-bold text-slate-900">{selected.plate}</div>
                    </div>
                    <div className="mt-1 text-sm text-slate-700">{selected.name || "—"}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{selected.model || "—"}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auto.assegnato_a")}</Label>
                      <Select
                        value={selected.technician_id || "__none__"}
                        onValueChange={(v) => reassignVehicle(selected.id, v === "__none__" ? null : v)}
                      >
                        <SelectTrigger className="w-64 rounded-sm" data-testid={`veh-reassign-${selected.id}`}>
                          <SelectValue placeholder={t("auto.nessuno")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t("auto.nessuno")}</SelectItem>
                          {technicians.map((tech) => (
                            <SelectItem key={tech.id} value={tech.id}>
                              <span className="flex items-center gap-2">
                                <span>{tech.name}</span>
                                <span className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${roleBadgeCls(tech.role)}`}>{roleLabel(tech.role)}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => deleteVehicle(selected.id)} data-testid={`veh-del-${selected.id}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>

              <div className="rounded-sm border border-slate-200 bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-[#0052FF]" />
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.documenti")}</div>
                    <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{docs.length}</span>
                  </div>
                  <Dialog open={openDoc} onOpenChange={setOpenDoc}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-veh-doc"><Plus className="mr-1 h-3.5 w-3.5" /> {t("auto.carica_documento")}</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{t("auto.nuovo_documento")} — {selected.plate}</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>{t("common.category")}</Label>
                          <Select value={docForm.category} onValueChange={(v) => setDocForm({ ...docForm, category: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="veh-doc-cat"><SelectValue /></SelectTrigger>
                            <SelectContent>{DOC_CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>{t("auto.titolo_req")}</Label><Input value={docForm.title} onChange={(e) => setDocForm({ ...docForm, title: e.target.value })} className="rounded-sm" data-testid="veh-doc-title" /></div>
                        <div><Label>{t("auto.scadenza_opzionale")}</Label><Input type="date" value={docForm.expiry_date} onChange={(e) => setDocForm({ ...docForm, expiry_date: e.target.value })} className="rounded-sm" data-testid="veh-doc-expiry" /></div>
                        <div><Label>File (PDF/JPG/PNG) *</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} className="rounded-sm" data-testid="veh-doc-file" /></div>
                      </div>
                      <DialogFooter><Button onClick={uploadDoc} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="veh-doc-save">{t("auto.carica")}</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                {docs.length === 0 && <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">{t("auto.nessun_documento_caricato")}</div>}
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {docs.map((d) => {
                    const cat = DOC_CATS.find((c) => c.value === d.category) || DOC_CATS[DOC_CATS.length - 1];
                    return (
                      <div key={d.id} className={`flex items-start gap-2 rounded-sm border p-3 ${cat.cls}`} data-testid={`veh-doc-${d.id}`}>
                        <FileText className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{cat.label}</div>
                          <div className="truncate text-sm font-semibold">{d.title}</div>
                          {d.expiry_date && <div className="text-[11px]">scad. {d.expiry_date}</div>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => downloadDoc(d)}><Download className="h-4 w-4 text-slate-700" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteDoc(d.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-sm border border-slate-200 bg-white p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.scadenze")}</div>
                    <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-xs">{deadlines.length}</span>
                  </div>
                  <Dialog open={openDl} onOpenChange={setOpenDl}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-sm bg-orange-600 hover:bg-orange-700" data-testid="btn-veh-dl"><Plus className="mr-1 h-3.5 w-3.5" /> {t("auto.aggiungi_scadenza")}</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>{t("auto.nuova_scadenza")} — {selected.plate}</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div>
                          <Label>{t("common.type")}</Label>
                          <Select value={dlForm.type} onValueChange={(v) => setDlForm({ ...dlForm, type: v })}>
                            <SelectTrigger className="rounded-sm" data-testid="veh-dl-type"><SelectValue /></SelectTrigger>
                            <SelectContent>{DEADLINE_TYPES.map((tech) => <SelectItem key={tech.value} value={tech.value}>{tech.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div><Label>{t("auto.data_scadenza_req")}</Label><Input type="date" value={dlForm.due_date} onChange={(e) => setDlForm({ ...dlForm, due_date: e.target.value })} className="rounded-sm" data-testid="veh-dl-date" /></div>
                        <div><Label>{t("common.notes")}</Label><Input value={dlForm.notes} onChange={(e) => setDlForm({ ...dlForm, notes: e.target.value })} className="rounded-sm" /></div>
                      </div>
                      <DialogFooter><Button onClick={createDeadline} className="rounded-sm bg-orange-600 hover:bg-orange-700" data-testid="veh-dl-save">{t("common.add")}</Button></DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
                {deadlines.length === 0 && <div className="rounded-sm border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">{t("auto.nessuna_scadenza_registrata")}</div>}
                <div className="space-y-2">
                  {deadlines.map((d) => {
                    const st = deadlineStatus(d);
                    const TypeIcon = (DEADLINE_TYPES.find((x) => x.value === d.type) || DEADLINE_TYPES[0]).icon;
                    return (
                      <div key={d.id} className={`flex flex-wrap items-center gap-3 rounded-sm border p-3 ${st.cls}`} data-testid={`veh-dl-${d.id}`}>
                        <TypeIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold uppercase tracking-wide">{d.type}</span>
                            <span className="rounded-sm border bg-white/60 px-1.5 py-0.5 text-[10px] font-semibold" data-testid={`veh-dl-status-${d.id}`}>{st.label}</span>
                          </div>
                          <div className="mt-0.5 text-xs">{t("auto.scadenza_label")} <b>{d.due_date}</b>{d.notes && <> · {d.notes}</>}</div>
                        </div>
                        {d.status === "pending" && (
                          <Button size="sm" onClick={() => markDone(d.id)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid={`veh-dl-done-${d.id}`}>
                            <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {t("auto.segna_eseguita")}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => deleteDeadline(d.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
